import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';

import {CATALOG_TYPES} from './constants';
import {getFormItems} from './mappers';
import {filterByCatalogType} from './mappers';
import types from './types';

const initialState = {
  templates: [],
  clientTemplates: [],
  clientTemplateNames: [],
  template: {
    form: {
      values: {
        name: '',
        form_pages: [],
      },
      initialFormItems: {},
      modifiedFormItems: {},
      errors: {},
    },
  },

  // virgin templates
  generalTemplates: [],
  formTemplates: [],
  gameTemplates: [],
  quizTemplates: [],

  // client templates
  generalClientTemplates: [],
  formClientTemplates: [],
  gameClientTemplates: [],
  quizClientTemplates: [],

  clientFonts: null,
  systemPages: [],

  // results
  result: null,
  initialResult: null,
  fetchingResult: false,

  // for path editor
  operationTemplates: {},
};

export default function (state = initialState, action) {
  switch (action.type) {
    case types.GET_TEMPLATES_SUCCESS: {
      const templates = action.payload.templates;

      return {
        ...state,
        templates,
        generalTemplates: filterByCatalogType(templates, CATALOG_TYPES.GENERAL),
        formTemplates: filterByCatalogType(templates, CATALOG_TYPES.FORM),
        gameTemplates: filterByCatalogType(templates, CATALOG_TYPES.GAME),
        quizTemplates: filterByCatalogType(templates, CATALOG_TYPES.QUIZ),
      };
    }

    case types.GET_CLIENT_TEMPLATES_SUCCESS: {
      const templates = action.payload.client_templates;

      return {
        ...state,
        clientTemplates: templates,
        generalClientTemplates: filterByCatalogType(templates, CATALOG_TYPES.GENERAL),
        formClientTemplates: filterByCatalogType(templates, CATALOG_TYPES.FORM),
        gameClientTemplates: filterByCatalogType(templates, CATALOG_TYPES.GAME),
        quizClientTemplates: filterByCatalogType(templates, CATALOG_TYPES.QUIZ),
      };
    }

    case types.GET_CLIENT_TEMPLATE_NAMES_SUCCESS: {
      return {
        ...state,
        clientTemplateNames: action.payload.client_template_names.map((i) => i.toLowerCase()),
      };
    }

    case types.GET_TEMPLATE_FORM_SUCCESS:
      return {
        ...state,
        template: {
          ...state.template,
          form: {
            ...state.template.form,
            values: {
              ...action.payload.form,
              form_pages: action.payload.form.form_pages,
            },
            initialFormItems: getFormItems(action.payload.form?.form_pages),
          },
        },
      };

    case types.GET_RESULT_BY_INTERFACE_REQUEST:
    case types.GET_RESULT_BY_CLIENT_TEMPLATE_REQUEST:
    case types.GET_RESULT_BY_OPERATION_TEMPLATE_REQUEST:
    case types.GET_RESULT_BY_EDITOR_OBJECT_REQUEST:
      return {
        ...state,
        fetchingResult: true,
      };

    case types.GET_RESULT_BY_INTERFACE_SUCCESS:
    case types.GET_RESULT_BY_CLIENT_TEMPLATE_SUCCESS:
    case types.GET_RESULT_BY_OPERATION_TEMPLATE_SUCCESS:
    case types.GET_RESULT_BY_EDITOR_OBJECT_SUCCESS: {
      const result = action.payload;
      return {
        ...state,
        initialResult: result,
        result,
        fetchingResult: false,
      };
    }

    case types.GET_RESULT_BY_INTERFACE_ERROR:
    case types.GET_RESULT_BY_CLIENT_TEMPLATE_ERROR:
    case types.GET_RESULT_BY_OPERATION_TEMPLATE_ERROR:
    case types.GET_RESULT_BY_EDITOR_OBJECT_ERROR:
      return {
        ...state,
        fetchingResult: false,
      };

    case types.UPDATE_RESULT:
      return {
        ...state,
        result: action.payload,
      };

    case types.RESET_RESULT:
      return {
        ...state,
        result: state.initialResult,
      };

    case types.SAVE_RESULT:
      return {
        ...state,
        initialResult: state.result,
      };

    case types.CLEAR_RESULT:
      return {
        ...state,
        result: null,
        initialResult: null,
        fetchingResult: false,
      };

    case types.CLEAR_CURRENT_TEMPLATE_FORM:
      return {
        ...state,
        template: {
          form: {
            values: {
              name: '',
              form_pages: [],
            },
            initialFormItems: {},
            modifiedFormItems: {},
            errors: {},
          },
        },
      };

    case types.SET_CURRENT_TEMPLATE_FORM_ERROR:
      const {formPageId, formSectionId, formItemId, err} = action.payload;
      const currentTemplateFormErrors = {
        ...state.template.form.errors,
      };

      const updatedErrors = set(
        cloneDeep(currentTemplateFormErrors),
        `'${formPageId}'.'${formSectionId}'.'${formItemId}'`,
        err,
      );
      return {
        ...state,
        template: {
          ...state.template,
          form: {
            ...state.template.form,
            errors: updatedErrors,
          },
        },
      };

    case types.SET_CURRENT_TEMPLATE_FORM_ANSWER:
      const formItem = action.payload;
      return {
        ...state,
        template: {
          ...state.template,
          form: {
            ...state.template.form,
            modifiedFormItems: {
              ...state.template.form.modifiedFormItems,
              ...formItem,
            },
          },
        },
      };

    case types.GET_CLIENT_FONTS_SUCCESS:
      return {
        ...state,
        clientFonts: action.payload.fonts,
      };

    case types.GET_OPERATION_TEMPLATES_SUCCESS: {
      const data = action.payload.operation_templates.filter((template) => {
        return template.catalog?.out_path || (template.row !== null && template.position !== null);
      });

      const {outPathPages, pathPages} = data.reduce(
        (acc, template) => {
          const updatedTemplate = {
            ...template,
            row: template.catalog?.out_path ? 0 : Math.max((template.row ?? 1) - 1, 0),
            position: Math.max((template.position ?? 1) - 1, 0),
          };

          if (template.catalog?.out_path) {
            acc.outPathPages.push(updatedTemplate);
          } else {
            acc.pathPages.push(updatedTemplate);
          }

          return acc;
        },
        {outPathPages: [], pathPages: []},
      );

      return {
        ...state,
        operationTemplates: {outPathPages, pathPages},
      };
    }

    case types.UPDATE_TEMPLATE_ANSWER_SUCCESS: {
      const {template_answer: templateAnswer} = action.payload;
      const associationFieldId = templateAnswer?.association_field_id;
      if (!associationFieldId) {
        return state;
      }

      const allTemplates = [...state.operationTemplates.pathPages, ...state.operationTemplates.outPathPages];
      const template = allTemplates.find((t) => t.id === associationFieldId);
      if (!template) {
        return state;
      }

      const keyPages = template.catalog?.out_path ? 'outPathPages' : 'pathPages';
      const pages = state.operationTemplates[keyPages] || [];

      const pageIndex = pages.findIndex((t) => t.id === template.id);

      const updatedTemplate = {
        ...template,
        delete_disabled: templateAnswer.association_field.delete_disabled,
        row: template.row - 1,
        position: template.position - 1,
      };

      let newPages;
      if (pageIndex !== -1) {
        newPages = pages.map((t, idx) =>
          idx === pageIndex ? {...t, delete_disabled: templateAnswer.association_field.delete_disabled} : t,
        );
      } else {
        newPages = [...pages, updatedTemplate];
      }

      return {
        ...state,
        operationTemplates: {
          ...state.operationTemplates,
          [keyPages]: cloneDeep(newPages),
        },
      };
    }

    case types.UPDATE_TEMPLATE_SUCCESS: {
      const template = action.payload?.template;
      const keyPages = template.catalog?.out_path ? 'outPathPages' : 'pathPages';
      const oldPages = state.operationTemplates[keyPages];
      if (oldPages) {
        const oldIndex = oldPages.findIndex((oldTemplate) => oldTemplate.id === template.id);
        const parsedTemplate = {...template, row: template.row - 1, position: template.position - 1};

        let newPages;
        if (oldIndex !== -1) {
          newPages = oldPages.map((t, idx) => (idx === oldIndex ? parsedTemplate : t));
        } else {
          newPages = [...oldPages, parsedTemplate];
        }

        return {
          ...state,
          operationTemplates: {
            ...state.operationTemplates,
            [keyPages]: cloneDeep(newPages),
          },
        };
      }
      return state;
    }

    case types.COPY_CLIENT_TEMPLATE_TO_PATH_SUCCESS: {
      const template = action.payload.operation_template;
      const keyPages = template.catalog?.out_path ? 'outPathPages' : 'pathPages';
      const currentPages = state.operationTemplates[keyPages] || [];
      const newTemplate = {...template, row: template.row - 1, position: template.position - 1};

      return {
        ...state,
        operationTemplates: {
          ...state.operationTemplates,
          [keyPages]: [...currentPages, newTemplate],
        },
      };
    }

    case types.MODIFY_OPERATION_TEMPLATES_PATH_SUCCESS: {
      const templates = action.payload.operation_templates;
      if (templates[0]) {
        const keyPages = templates[0].catalog?.out_path ? 'outPathPages' : 'pathPages';
        const oldPages = state.operationTemplates[keyPages] || [];

        const newPages = templates.reduce(
          (acc, template) => {
            const oldIndex = acc.findIndex((oldTemplate) => oldTemplate.id === template.id);
            const parsedTemplate = {...template, row: template.row - 1, position: template.position - 1};
            if (oldIndex !== -1) {
              return acc.map((t, idx) => (idx === oldIndex ? parsedTemplate : t));
            }
            return [...acc, parsedTemplate];
          },
          [...oldPages],
        );

        return {
          ...state,
          operationTemplates: {
            ...state.operationTemplates,
            [keyPages]: cloneDeep(newPages),
          },
        };
      }
      return state;
    }

    case types.DELETION_OPERATION_TEMPLATES_PATH_SUCCESS: {
      const templates = action.payload.operation_templates;

      if (!templates || templates.length === 0) {
        return state;
      }

      const keyPages = templates[0].catalog?.out_path ? 'outPathPages' : 'pathPages';

      return {
        ...state,
        operationTemplates: {
          ...state.operationTemplates,
          [keyPages]: templates.map((template) => ({
            ...template,
            row: template.row - 1,
            position: template.position - 1,
          })),
        },
      };
    }

    case types.REMOVE_TEMPLATE_FROM_PATH_SUCCESS: {
      const templateId = action.payload?.id;
      const data = [...state.operationTemplates.pathPages, ...state.operationTemplates.outPathPages];
      const template = data.find((t) => t.id === templateId);

      const keyPages = template.catalog?.out_path ? 'outPathPages' : 'pathPages';

      if (!templateId) {
        return {
          ...state,
          operationTemplates: state.operationTemplates,
        };
      }

      return {
        ...state,
        operationTemplates: {
          ...state.operationTemplates,
          [keyPages]: state.operationTemplates[keyPages].filter((t) => t.id !== templateId),
        },
      };
    }

    case types.GET_SYSTEM_PAGES_SUCCESS:
      return {
        ...state,
        systemPages: action.payload.operation_templates,
      };

    default:
      return state;
  }
}
